import { createRouter, createWebHistory } from 'vue-router';
import HomeView from '@/views/HomeView.vue';
import DashboardView from '@/views/DashboardView.vue';
const LoginView = () => import('@/views/LoginView.vue');
const AuthExchangeView = () => import('@/views/AuthExchangeView.vue');
const ShieldCallbackView = () => import('@/views/ShieldCallbackView.vue');
const WelcomeView = () => import('@/views/WelcomeView.vue');
import auth from '@/services/auth';
import { IdleManager } from '@/utils/idle';

// Lazy-load admin layout and management views
const AdminLayout = () => import('@/layouts/AdminLayout.vue');
const UsersView = () => import('@/views/UsersView.vue');
const LocationsView = () => import('@/views/LocationsView.vue');
const RolesView = () => import('@/views/RolesView.vue');
const QueriesView = () => import('@/views/admin/QueriesView.vue');
const QueryRevisionsView = () => import('@/views/admin/QueryRevisionsView.vue');
const ChangelogView = () => import('@/views/ChangelogView.vue');

const routes = [
  { path: '/', name: 'landing', component: HomeView },
  { path: '/home', name: 'home', component: WelcomeView, meta: { requiresAuth: true, title: 'Home' } },
  { path: '/login', name: 'login', component: LoginView },
  { path: '/admin/login', redirect: '/login' },
  { path: '/auth/exchange', name: 'auth-exchange', component: AuthExchangeView, meta: { title: 'Authenticating…' } },
  { path: '/shield-callback', name: 'shield-callback', component: ShieldCallbackView, meta: { title: 'Shield Login...' } },
  { path: '/dashboard', name: 'dashboard', component: DashboardView, meta: { requiresAuth: true, title: 'Dashboard' } },

  // Admin parent route (admin-only)
  {
    path: '/admin',
    name: 'admin',
    component: AdminLayout,
    meta: { requiresAuth: true, roles: ['Admin'], title: 'Administration', nav: true },
    children: [
      { path: 'users', name: 'admin-users', component: UsersView, meta: { title: 'Users', nav: true, roles: ['Admin'], icon: 'bi-people' } },
      { path: 'locations', name: 'admin-locations', component: LocationsView, meta: { title: 'Locations', nav: true, roles: ['Admin'], icon: 'bi-geo-alt' } },
      { path: 'roles', name: 'admin-roles', component: RolesView, meta: { title: 'Roles', nav: true, roles: ['Admin'], icon: 'bi-shield-lock' } },
      { path: 'queries', name: 'admin-queries', component: QueriesView, meta: { title: 'Queries', nav: true, roles: ['Admin'], icon: 'bi-database' } },
      { path: 'queries/:id/revisions', name: 'admin-query-revisions', component: QueryRevisionsView, meta: { title: 'Revisions', nav: false, roles: ['Admin'] } },
      { path: 'email-templates', name: 'admin-email-templates', component: () => import('@/views/admin/EmailTemplatesView.vue'), meta: { title: 'Email Templates', nav: true, roles: ['Admin'], icon: 'bi-envelope' } },
      { path: 'email-templates/:id/revisions', name: 'admin-email-template-revisions', component: () => import('@/views/admin/EmailTemplateRevisionsView.vue'), meta: { title: 'Email Template Revisions', nav: false, roles: ['Admin'] } },
      { path: 'prompts', name: 'admin-prompts', component: () => import('@/views/PromptsView.vue'), meta: { title: 'AI Prompts', nav: true, roles: ['Admin'], icon: 'bi-chat-square-text' } },
      { path: 'prompts-templates', name: 'admin-prompts-templates', component: () => import('@/views/admin/PromptsManagementView.vue'), meta: { title: 'Prompt Templates (Legacy)', nav: false, roles: ['Admin'], icon: 'bi-file-text' } },
      { path: 'prompts-templates/:id/revisions', name: 'admin-prompt-revisions', component: () => import('@/views/admin/PromptRevisionsView.vue'), meta: { title: 'Prompt Revisions', nav: false, roles: ['Admin'] } },
      { path: 'changelog', name: 'admin-changelog', component: ChangelogView, meta: { title: 'Changelog', nav: true, roles: ['Admin'], icon: 'bi-list-ul' } },
      { path: 'analytics', name: 'admin-analytics', component: () => import('@/views/AnalyticsView.vue'), meta: { title: 'GA4 Analytics', nav: true, roles: ['Admin'], icon: 'bi-bar-chart' } },
      { path: 'soci-analytics', name: 'admin-soci-analytics', component: () => import('@/views/SOCiAnalyticsView.vue'), meta: { title: 'SOCi Analytics', nav: true, roles: ['Admin'], icon: 'bi-share' } },
      { path: 'pam-analytics', name: 'admin-pam-analytics', component: () => import('@/views/PAMUsageAnalyticsView.vue'), meta: { title: 'PAM Analytics', nav: true, roles: ['Admin'], icon: 'bi-graph-up' } },
      { path: 'meta-analytics', name: 'admin-meta-analytics', component: () => import('@/views/MetaAnalyticsView.vue'), meta: { title: 'Meta Analytics', nav: true, roles: ['Admin'], icon: 'bi-meta' } },
      { path: 'sentiment', name: 'admin-sentiment', component: () => import('@/views/SentimentView.vue'), meta: { title: 'Sentiment', nav: true, roles: ['Admin'], icon: 'bi-star-fill' } },
      { path: 'system-status', name: 'admin-system-status', component: () => import('@/views/SystemStatusView.vue'), meta: { title: 'System Status', nav: true, roles: ['Admin'], icon: 'bi-activity' } },
      { path: 'user-feedback', name: 'admin-user-feedback', component: () => import('@/views/UserFeedbackView.vue'), meta: { title: 'User Feedback', nav: true, roles: ['Admin'], icon: 'bi-chat-square-text' } },
      { path: 'api-docs', name: 'admin-api-docs', meta: { title: 'PAM API Docs', nav: true, roles: ['Admin'], icon: 'bi-file-earmark-code', externalUrl: '/pam/api/docs', target: '_blank' } },
    ],
  },

  // Legacy management routes redirect to admin
  { path: '/management', redirect: '/admin' },
  { path: '/management/:pathMatch(.*)*', redirect: to => `/admin/${to.params.pathMatch}` },

  // Legacy prompts-management redirect to new prompts route
  { path: '/admin/prompts-management', redirect: '/admin/prompts' },

  { path: '/forbidden', name: 'forbidden', component: () => import('@/views/ForbiddenView.vue'), meta: { title: 'Forbidden' } },
  { path: '/:pathMatch(.*)*', redirect: '/' },
];

const router = createRouter({
  history: createWebHistory('/'),
  routes,
});

// Signal activity on every route change (helps keep session alive during navigation)
router.afterEach(() => {
  IdleManager.signalActivity();
});

// Fetch session token if not already authenticated
let sessionTokenFetched = false;
async function ensureSessionToken() {
  if (sessionTokenFetched || auth.isAuthenticated()) {
    return;
  }
  sessionTokenFetched = true;

  try {
    const response = await fetch('/pam/api/session-token', {
      credentials: 'include'
    });

    if (response.ok) {
      const data = await response.json();
      if (data.access_token && data.user) {
        localStorage.setItem('auth.token', data.access_token);
        localStorage.setItem('auth.user', JSON.stringify(data.user));
      }
    }
  } catch (e) {
    // Silently fail - user will need to login via Vue app
    console.log('No session available:', e);
  }
}

router.beforeEach(async (to, from, next) => {
  // Try to fetch session token before checking auth
  await ensureSessionToken();

  if (to.meta.requiresAuth && !auth.isAuthenticated()) {
    const redirect = to.fullPath && to.fullPath !== '/' ? encodeURIComponent(to.fullPath) : '';
    // Redirect to /login with proper redirect parameter
    return next({ path: '/login', query: redirect ? { redirect } : {} });
  }

  // Enforce role-based access if meta.roles is set
  const roles = (to.meta && to.meta.roles) || [];
  if (roles.length) {
    const user = auth.getUser();
    const userRoles = Array.isArray(user?.roles) ? user.roles : [];
    const userRolesLower = userRoles.map(r => r.toLowerCase());
    const isAdmin = user?.is_admin === true || userRolesLower.includes('admin');
    const allowed = roles.some(r => r.toLowerCase() === 'admin') ? isAdmin : roles.some(r => userRolesLower.includes(r.toLowerCase()));
    if (!allowed) {
      return next({ name: 'forbidden', query: { from: to.fullPath } });
    }
  }

  if ((to.name === 'login') && auth.isAuthenticated()) {
    // If already logged in, avoid showing login again
    return next({ name: 'dashboard' });
  }
  return next();
});

export default router;
